import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { MotifIconButton } from '../button/icon-button/icon-button';
import { TreeContext } from './treeContext';
import { ReactComponent as NavigationIcChevronRight24px } from '../../assets/icons/navigation/ic_chevron_right_24px.svg';
import { ReactComponent as ContentIcRemove24px } from '../../assets/icons/content/ic_remove_24px.svg';
import { ReactComponent as ContentIcAdd24px } from '../../assets/icons/content/ic_add_24px.svg';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {React.ReactNode} [value]
 * @prop {boolean} [open]
 * @prop {() => void} [onClick]
 * @prop {boolean} [defaultExpandAll]
 * @prop {string} [className]
 * @prop {{close: React.ReactNode, open: React.ReactNode}} [icons]
 */
/**
 * @param {Props} props
 */
const MotifTreeNode = ({
  value = null,
  open = false,
  onClick = null,
  children,
  className = '',
  icons = {}
}) => {
  const { defaultExpandAll, expandAll, showLines, disabled } = useContext(TreeContext);
  const [showList, setShowList] = useState(defaultExpandAll);
  const [visible, setVisible] = useState(false);

  const handleClick = () => {
    setVisible(!visible);
    if (onClick) {
      onClick();
    }
  };

  useEffect(() => {
    setVisible(expandAll || showList || open);
  }, [expandAll, open]);

  useEffect(() => {
    setShowList(false);
  }, []);

  const classes = classNames({
    'motif-tree-node': true,
    'motif-tree-node-visible': visible,
    'motif-tree-node-lines': showLines,
    [className]: true
  });

  const chevronArrow = (
    <NavigationIcChevronRight24px style={{ transform: visible ? 'rotate(90deg)' : null }} />
  );

  const plusMinusIcons = visible ? <ContentIcRemove24px /> : <ContentIcAdd24px />;

  const customIcon = visible ? icons.close : icons.open;
  const defaultIcon = showLines ? plusMinusIcons : chevronArrow;

  const buttonExpandCollapse = children ? (
    <MotifIconButton
      className="motif-tree-node-button"
      aria-label={visible ? 'expand tree' : 'collapse tree'}
      type="button"
      onClick={handleClick}
      disabled={disabled}
    >
      {customIcon || defaultIcon}
    </MotifIconButton>
  ) : null;

  return (
    <li className={classes}>
      <div className="motif-tree-node-content">
        {buttonExpandCollapse}
        {value}
      </div>

      {children && <ul className="motif-tree-nodes">{children}</ul>}
    </li>
  );
};

MotifTreeNode.propTypes = {
  children: PropTypes.node,
  value: PropTypes.node,
  open: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  icons: PropTypes.shape({
    close: PropTypes.node,
    open: PropTypes.node
  })
};

export { MotifTreeNode };
